import React, { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  BookLocationValuesContext,
  BookValuesContext,
  DRMValuesContext,
  DocumentValuesContext,
  UserContentItemModifiersContext,
  UserContentItemValuesContext,
} from 'contexts';
import { useDimensions } from 'hooks';
import {
  AnnotationModal,
  FlashcardModal,
  CopyContentModal,
  CitationModal,
} from 'components';
import { ANNOTATION_MODALS } from 'constants';

const DocumentModals = ({
  bookLocationForModalCoordinates,
  highlightToFocusRef,
  onReadFromHere,
  setBookLocationForModalCoordinates,
}) => {
  const { frame, frameDocument, getRangeForCfi, frameContainer } = useContext(
    DocumentValuesContext,
  );
  const { book } = useContext(BookValuesContext);
  const { currentBookLocation } = useContext(BookLocationValuesContext);

  const drmValues = useContext(DRMValuesContext);

  const {
    flashcardToEdit,
    isCitationModalOpen,
    noteToEdit,
    selectedCopiedText,
    selectedHighlight,
    selectedNote,
  } = useContext(UserContentItemValuesContext);

  const userContentItemModifiers = useContext(UserContentItemModifiersContext);
  const {
    clearAllUserContent,
    setFlashcardToEdit,
    setIsCitationModalOpen,
    setSelectedCopiedText,
  } = userContentItemModifiers;

  const { dimensions } = useDimensions(frameContainer);

  // Modal Positioning
  const [modalCoordinates, setModalCoordinates] = useState({
    top: 0,
    left: 0,
  });

  // Allow any BookLocation to dictate modal positioning
  useEffect(
    function computeModalCoordinates() {
      if (!bookLocationForModalCoordinates) {
        return;
      }

      let top = 0;
      let left = 0;

      if (
        bookLocationForModalCoordinates?.document ===
          currentBookLocation?.document &&
        frameDocument
      ) {
        const range = getRangeForCfi(bookLocationForModalCoordinates.cfi);

        const clientRects = range.getClientRects();

        if (clientRects.length) {
          const modalAnchorRect = clientRects.length - 1; // use the last one

          top =
            clientRects[modalAnchorRect].top +
            clientRects[modalAnchorRect].height +
            frame.getBoundingClientRect().top;

          left =
            clientRects[modalAnchorRect].left +
            clientRects[modalAnchorRect].width / 2 + // put it in the middle of the text selection
            frame.getBoundingClientRect().left;
        }
      }

      setModalCoordinates({
        top,
        left,
      });
    },
    [
      bookLocationForModalCoordinates,
      currentBookLocation?.document,
      dimensions,
      frame,
      frameDocument,
      getRangeForCfi,
    ],
  );

  // Use the highlight's position to compute modal position
  useEffect(
    function computeModalPositionByHighlight() {
      if (selectedHighlight) {
        setBookLocationForModalCoordinates(selectedHighlight.bookLocation);
      } else {
        setBookLocationForModalCoordinates(null);
      }
    },
    [selectedHighlight, setBookLocationForModalCoordinates],
  );

  // Flashcard Functions
  const onFlashcardSave = async (properties) =>
    await flashcardToEdit.change(properties).save();

  // Close Modals + Choose Focus
  const [modalOptionToFocus, setModalOptionToFocus] = useState(null);

  const handleOnFlashcardClose = () => {
    setFlashcardToEdit(null);
    setModalOptionToFocus(ANNOTATION_MODALS.flashcard);
  };

  const handleOnCopyClose = () => {
    setSelectedCopiedText(null);
    setModalOptionToFocus(ANNOTATION_MODALS.copy);
  };

  const handleOnCitationClose = useCallback(() => {
    // Exit the citation menu
    setIsCitationModalOpen(false);
    setModalOptionToFocus(ANNOTATION_MODALS.citation);
  }, [setIsCitationModalOpen, setModalOptionToFocus]);

  return (
    <>
      {selectedHighlight &&
        !isCitationModalOpen &&
        !flashcardToEdit &&
        !selectedCopiedText &&
        !selectedNote &&
        !noteToEdit && (
          // Main annotation modal to display when not on a submenu.
          <AnnotationModal
            anchorPosition={modalCoordinates}
            bookLocation={currentBookLocation}
            highlight={selectedHighlight}
            highlightToFocusRef={highlightToFocusRef}
            modalOptionToFocus={modalOptionToFocus}
            onClose={clearAllUserContent}
            onReadFromHere={onReadFromHere}
            setModalOptionToFocus={setModalOptionToFocus}
          />
        )}
      {flashcardToEdit && (
        // Flashcard submenu.
        <FlashcardModal
          anchorPosition={modalCoordinates}
          flashcard={flashcardToEdit}
          onClose={handleOnFlashcardClose}
          onCloseAll={clearAllUserContent}
          onSave={onFlashcardSave}
        />
      )}
      {selectedCopiedText && !selectedCopiedText.id && (
        // Copy submenu (new copy)
        <CopyContentModal
          copiedText={selectedCopiedText}
          anchorPosition={modalCoordinates}
          availablePercent={drmValues.copyPercentMaximum}
          canCopy={drmValues.allowedToCopyAdditionalCharacters(
            selectedCopiedText.snippet.length,
          )}
          currentPercent={drmValues.copyPercentUsed}
          newPercent={book.getPercentageOfBookForCharacterCount(
            selectedCopiedText.snippet.length,
          )}
          onClose={handleOnCopyClose}
          onCloseAll={clearAllUserContent}
          remainingAllowance={drmValues.copyPercentRemaining}
        />
      )}
      {selectedCopiedText?.id && (
        // Copy submenu (existing copy)
        <CopyContentModal
          copiedText={selectedCopiedText}
          anchorPosition={modalCoordinates}
          availablePercent={drmValues.copyPercentMaximum}
          canCopy={true}
          currentPercent={drmValues.copyPercentUsed}
          onClose={handleOnCopyClose}
          onCloseAll={clearAllUserContent}
          remainingAllowance={drmValues.copyPercentRemaining}
        />
      )}
      {selectedHighlight && isCitationModalOpen && (
        // Citation submenu.
        <CitationModal
          anchorPosition={modalCoordinates}
          book={book}
          onClose={handleOnCitationClose}
          onCloseAll={clearAllUserContent}
        />
      )}
    </>
  );
};

DocumentModals.propTypes = {
  bookLocationForModalCoordinates: PropTypes.shape({}),
  highlightToFocusRef: PropTypes.shape({}),
  onReadFromHere: PropTypes.func,
  setBookLocationForModalCoordinates: PropTypes.func,
};

export default DocumentModals;
